import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Lock } from "lucide-react";
import { toast } from "sonner";
import { useDriveAuth } from "../context/DriveAuthContext";
import { LOGO_URL } from "../../lib/data";

export default function DriveLogin() {
  const { signIn, signUp } = useDriveAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setBusy(true);
    try {
      if (mode === "signup") {
        await signUp(email, password);
        toast.success("Account erstellt. Prüfe ggf. deine E-Mails zur Verifizierung.");
      } else {
        await signIn(email, password);
        toast.success("Eingeloggt");
        nav("/drive/hub");
      }
    } catch (err) {
      toast.error(err.message || "Fehler bei der Anmeldung");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6 grain">
      <div className="absolute top-6 left-6">
        <Link to="/" className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase font-semibold text-zinc-500 hover:text-white transition-colors">
          <ArrowLeft size={12} /> Zur Website
        </Link>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
        className="w-full max-w-md relative"
      >
        <div className="absolute -top-20 -left-20 w-72 h-72 rounded-full bg-[#ff0033]/15 blur-[120px] pointer-events-none" />

        <div className="relative bg-[#0A0A0A] border border-white/[0.08] p-8 md:p-10">
          <div className="flex flex-col items-center mb-8">
            <img src={LOGO_URL} alt="STRAßENWERT" className="h-14 w-auto mb-4 drop-shadow-[0_0_18px_rgba(255,0,51,0.5)]" />
            <div className="flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase text-zinc-500 font-bold">
              <Lock size={11} className="text-[#ff0033]" /> Drive · Interner Bereich
            </div>
          </div>

          <h1 className="font-display font-black uppercase text-3xl md:text-4xl tracking-tighter text-center mb-8">
            {mode === "signin" ? "Anmelden" : "Registrieren"}
          </h1>

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">E-Mail</label>
              <input
                data-testid="drive-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-transparent border-b border-white/15 py-3 text-white outline-none focus:border-[#ff0033]/60 transition-colors"
                placeholder="name@email.com"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">Passwort</label>
              <input
                data-testid="drive-password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-transparent border-b border-white/15 py-3 text-white outline-none focus:border-[#ff0033]/60 transition-colors"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={busy}
              data-testid="drive-submit"
              className="group relative w-full inline-flex items-center justify-center gap-3 bg-white text-black px-7 py-4 text-[11px] font-bold tracking-[0.3em] uppercase overflow-hidden hover:shadow-[0_0_28px_rgba(255,0,51,0.45)] transition-all disabled:opacity-50"
            >
              <span className="relative z-10 group-hover:text-white transition-colors">
                {busy ? "Bitte warten…" : mode === "signin" ? "Einloggen" : "Account erstellen"}
              </span>
              <span className="absolute inset-0 bg-[#ff0033] translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
            </button>
          </form>

          <div className="mt-6 text-center text-xs text-zinc-500">
            {mode === "signin" ? "Noch kein Account?" : "Bereits registriert?"}{" "}
            <button
              type="button"
              onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
              className="text-white hover:text-[#ff0033] underline decoration-[#ff0033]/50 underline-offset-2"
            >
              {mode === "signin" ? "Registrieren" : "Anmelden"}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
